import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Truck, CheckCircle, AlertTriangle, Clock, Plus, Search, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending_approval: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  inactive: 'bg-zinc-600 text-zinc-300', maintenance: 'bg-red-500/20 text-red-400 border-red-500/30',
  decommissioned: 'bg-zinc-700 text-muted-foreground',
};

export default function AdminEquipment() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchData = () => {
    api.get('/admin/fleet-overview')
      .then(r => setEquipment(r.data))
      .catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleApprove = async (equipmentId) => {
    try {
      await api.put(`/equipment/${equipmentId}/approve`);
      toast.success('Equipment approved');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
  };

  const pending = equipment.filter(e => e.status === 'pending_approval');
  const active = equipment.filter(e => e.status === 'active');
  const withIssues = equipment.filter(e => e.docs_expiring > 0);

  // Apply filters
  let filtered = equipment;
  if (typeFilter !== 'all') filtered = filtered.filter(e => e.equipment_type === typeFilter);
  if (statusFilter !== 'all') filtered = filtered.filter(e => e.status === statusFilter);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(e =>
      e.make?.toLowerCase().includes(q) || e.model?.toLowerCase().includes(q) ||
      e.owner_name?.toLowerCase().includes(q) || e.vin?.toLowerCase().includes(q) ||
      e.license_plate?.toLowerCase().includes(q) || e.unit_number?.toLowerCase().includes(q)
    );
  }

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Fleet Equipment</h1>
          <p className="text-muted-foreground text-sm">{equipment.length} total</p>
        </div>
        <AddEquipmentDialog open={createOpen} onOpenChange={setCreateOpen} token={token} user={user} onCreated={fetchData} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Equipment', value: equipment.length, accent: 'text-foreground' },
          { label: 'Active', value: active.length, accent: 'text-emerald-400' },
          { label: 'Pending Approval', value: pending.length, accent: 'text-amber-400' },
          { label: 'Needs Attention', value: withIssues.length, accent: 'text-red-400' },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border"><CardContent className="p-4 text-center">
            <p className={`text-2xl font-bold ${s.accent}`}>{s.value}</p>
            <p className="text-[10px] text-muted-foreground mt-1">{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Pending Approvals */}
      {pending.length > 0 && (
        <Card className="bg-card border-amber-500/30">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-400 flex items-center gap-2"><Clock className="w-4 h-4" /> Pending Approvals ({pending.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pending.map(eq => (
                <div key={eq.equipment_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Truck className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{eq.make || eq.equipment_type} {eq.model || ''} {eq.year || ''}</p>
                      <p className="text-xs text-muted-foreground">Owner: {eq.owner_name} · {eq.equipment_type}{eq.unit_number ? ` · #${eq.unit_number}` : ''}</p>
                    </div>
                  </div>
                  <Button size="sm" className="bg-emerald-600 text-foreground h-7 text-xs" onClick={() => handleApprove(eq.equipment_id)}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search equipment..."
            className="w-full bg-muted border border-input text-foreground text-xs rounded-lg pl-8 pr-3 py-2" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="bg-muted border border-input text-muted-foreground text-xs rounded-lg px-3 py-2 appearance-none cursor-pointer">
          <option value="all">All Types</option>
          {['truck', 'trailer', 'reefer', 'flatbed'].map(t => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-muted border-input text-muted-foreground w-36 rounded-lg text-xs">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-muted border-input">
            <SelectItem value="all" className="text-foreground">All Status</SelectItem>
            {['active', 'pending_approval', 'inactive', 'maintenance', 'decommissioned'].map(s => (
              <SelectItem key={s} value={s} className="text-foreground">{s.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Equipment Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">EQUIPMENT</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">OWNER</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">TYPE</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">STATUS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">DOCS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">LAST INSPECTION</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(eq => (
                <TableRow key={eq.equipment_id} className="border-border hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/admin/equipment/${eq.equipment_id}`)}>
                  <TableCell>
                    <p className="text-xs text-foreground font-medium">{eq.make || eq.equipment_type} {eq.model || ''}</p>
                    <p className="text-[10px] text-muted-foreground">{eq.year || ''}{eq.unit_number ? ` · #${eq.unit_number}` : ''}{eq.vin ? ` · ${eq.vin.slice(-6)}` : ''}</p>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{eq.owner_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground capitalize">{eq.equipment_type}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${STATUS_COLORS[eq.status]}`}>{eq.status?.replace('_', ' ')}</Badge></TableCell>
                  <TableCell>
                    {eq.docs_expiring > 0 ? <span className="text-red-400 text-[10px] flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{eq.docs_expiring} expiring</span> :
                     <span className="text-emerald-400 text-[10px]">{eq.documents?.length || 0} docs</span>}
                  </TableCell>
                  <TableCell>
                    {eq.last_inspection ?
                      <span className={`text-[10px] ${eq.last_inspection.defects_found ? 'text-red-400' : 'text-emerald-400'}`}>
                        {eq.last_inspection.inspection_date} · {eq.last_inspection.overall_status}
                      </span> : <span className="text-muted-foreground text-[10px]">None</span>}
                  </TableCell>
                  <TableCell><ChevronRight className="w-3.5 h-3.5 text-muted-foreground" /></TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow className="border-border"><TableCell colSpan={7} className="text-center text-muted-foreground text-sm py-8">No equipment matches filters</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Equipment Detail */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-surface border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.make} {detail?.model} {detail?.year}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Type', value: detail.equipment_type },
                  { label: 'VIN', value: detail.vin || 'N/A' },
                  { label: 'Plate', value: `${detail.license_plate || 'N/A'} ${detail.license_plate_province || ''}` },
                  { label: 'Owner', value: detail.owner_name },
                  { label: 'Unit #', value: detail.unit_number || 'N/A' },
                  { label: 'Status', value: detail.status?.replace('_', ' ') },
                ].map((r, i) => (
                  <div key={i} className="bg-muted/50 rounded-lg p-2">
                    <p className="text-[10px] text-muted-foreground">{r.label}</p>
                    <p className="text-xs text-foreground capitalize">{r.value}</p>
                  </div>
                ))}
              </div>

              {/* Documents */}
              {detail.documents?.length > 0 && (
                <div>
                  <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-2">DOCUMENTS</p>
                  <div className="space-y-1.5">
                    {detail.documents.map((d, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <span className="text-xs text-muted-foreground capitalize">{d.document_type?.replace('_', ' ')}</span>
                        <div className="flex items-center gap-2">
                          {d.expiry_date && <span className="text-[10px] text-muted-foreground">{d.expiry_date}</span>}
                          <Badge className={`text-[10px] ${d.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : d.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>{d.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Maintenance History */}
              {detail.maintenance?.length > 0 && (
                <div>
                  <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-2">MAINTENANCE HISTORY</p>
                  <div className="space-y-1.5">
                    {detail.maintenance.map((m, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <div>
                          <p className="text-xs text-foreground capitalize">{m.maintenance_type?.replace('_', ' ')}</p>
                          <p className="text-[10px] text-muted-foreground">{m.description}</p>
                        </div>
                        <div className="text-right">
                          {m.cost && <p className="text-xs text-brand-accent">${m.cost}</p>}
                          <Badge className={`text-[10px] ${m.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>{m.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.status === 'pending_approval' && (
                <Button className="w-full bg-emerald-600 hover:bg-emerald-500" onClick={() => { handleApprove(detail.equipment_id); setDetail(null); }}>
                  <CheckCircle className="w-4 h-4 mr-2" /> Approve Equipment
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddEquipmentDialog({ open, onOpenChange, token, user, onCreated }) {
  const [form, setForm] = useState({ make: '', model: '', year: '', equipment_type: 'truck', vin: '', license_plate: '', license_plate_province: 'ON', unit_number: '' });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.make) { toast.error('Make is required'); return; }
    setSaving(true);
    try {
      await api.post('/equipment', { ...form, year: form.year ? parseInt(form.year) : null });
      toast.success('Equipment added');
      onOpenChange(false);
      setForm({ make: '', model: '', year: '', equipment_type: 'truck', vin: '', license_plate: '', license_plate_province: 'ON', unit_number: '' });
      onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-brand-accent hover:bg-yellow-500 text-brand-accent-foreground font-bold rounded-lg text-xs tracking-wide h-9 px-4">
          <Plus className="w-4 h-4 mr-1" /> ADD EQUIPMENT
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-border text-foreground max-w-md">
        <DialogHeader><DialogTitle className="tracking-wider">Add Equipment</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div><Label className="text-muted-foreground text-xs">Make</Label>
            <Input value={form.make} onChange={e => setForm({ ...form, make: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="Freightliner" /></div>
          <div><Label className="text-muted-foreground text-xs">Model</Label>
            <Input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="Cascadia" /></div>
          <div><Label className="text-muted-foreground text-xs">Year</Label>
            <Input type="number" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="2024" /></div>
          <div><Label className="text-muted-foreground text-xs">Vehicle Type</Label>
            <select value={form.equipment_type} onChange={e => setForm({ ...form, equipment_type: e.target.value })}
              className="w-full mt-1 bg-muted border border-input text-foreground text-sm rounded-lg px-3 py-2">
              {['truck', 'trailer', 'reefer', 'flatbed', 'tanker'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select></div>
          <div><Label className="text-muted-foreground text-xs">VIN</Label>
            <Input value={form.vin} onChange={e => setForm({ ...form, vin: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="Optional" /></div>
          <div><Label className="text-muted-foreground text-xs">License Plate</Label>
            <Input value={form.license_plate} onChange={e => setForm({ ...form, license_plate: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="ABC 1234" /></div>
          <div><Label className="text-muted-foreground text-xs">Province</Label>
            <Input value={form.license_plate_province} onChange={e => setForm({ ...form, license_plate_province: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="ON" /></div>
          <div><Label className="text-muted-foreground text-xs">Unit Number</Label>
            <Input value={form.unit_number} onChange={e => setForm({ ...form, unit_number: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="T-004" /></div>
        </div>
        <Button onClick={handleCreate} disabled={saving} className="w-full bg-brand-accent hover:bg-yellow-500 text-brand-accent-foreground font-bold rounded-lg mt-4 tracking-wider">
          {saving ? 'ADDING...' : 'ADD EQUIPMENT'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
